import React from "react";
import { Link } from "react-router-dom";
import { Award, Lock, Clock, Sparkles, Target, Compass, ShieldCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { TEAM } from "../data/content";

export default function About() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div data-testid="about-page">
      <SEO
        path="/about"
        titleAr="عن مجموعة الدخيل القانونية | مكتب محاماة مرخص في الكويت"
        titleEn="About Aldakheel Legal Group | Kuwait-Licensed Law Firm"
        descriptionAr="تعرّف على مجموعة الدخيل القانونية: مكتب محاماة كويتي مرخص، خبرة 9+ سنوات، 2000+ قضية، مهمتنا، رؤيتنا، قيمنا، وفريقنا القانوني."
        descriptionEn="Learn about Aldakheel Legal Group: Kuwait-licensed law firm, 9+ years experience, 2,000+ matters. Our mission, vision, values, and team."
        jsonLd={[orgJsonLd(), breadcrumbJsonLd([
          { name: isAr ? "الرئيسية" : "Home", path: "/" },
          { name: isAr ? "من نحن" : "About", path: "/about" },
        ])]}
      />

      {/* HERO */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe text-center max-w-3xl">
          <p className="eyebrow">{isAr ? "من نحن" : "About"}</p>
          <h1 className={`mt-4 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)", lineHeight: 1.2 }}>
            {isAr ? "مكتب كويتي مبني حول قضيتك" : "A Kuwaiti firm built around your matter"}
          </h1>
          <p className={`mt-8 ${isAr ? "font-arabic" : ""} text-lg leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
            {isAr
              ? "مجموعة الدخيل القانونية مكتب محاماة واستشارات قانونية مرخّص في دولة الكويت. نحضر عن عملائنا أمام درجات القضاء الكويتي كافة، ونقدّم المشورة للمؤسسين والعائلات والشركات في القضايا التي تصوغ مستقبلهم."
              : "Aldakheel Legal Group is a Kuwait-licensed advocacy and consultancy practice. We appear before every degree of the Kuwaiti judiciary and counsel founders, families, and businesses on the matters that shape their futures."}
          </p>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe grid lg:grid-cols-2 gap-8">
          <div className="luxe-card p-10">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-6"
                 style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
              <Target className="w-6 h-6" />
            </div>
            <p className="eyebrow">{isAr ? "المهمة" : "Mission"}</p>
            <h2 className={`mt-2 ${isAr ? "font-arabic" : "font-display"} text-2xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "نترجم النص القانوني إلى قرار" : "We translate law into decisions"}
            </h2>
            <p className={`mt-5 ${isAr ? "font-arabic" : ""} leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
              {isAr
                ? "تقديم استشارة قانونية كويتية صريحة، تراعي الواقع التجاري، ومستدامة — راسخة في نصوص القانون واجتهاد محكمة التمييز المستقر — تساعدك على اتخاذ القرارات الصحيحة في حياتك العائلية والمهنية."
                : "Deliver candid, commercially literate, and durable Kuwaiti legal counsel — anchored in statute and settled Cassation jurisprudence — that helps you make the right decisions in your personal and professional life."}
            </p>
          </div>
          <div className="luxe-card p-10">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-6"
                 style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
              <Compass className="w-6 h-6" />
            </div>
            <p className="eyebrow">{isAr ? "الرؤية" : "Vision"}</p>
            <h2 className={`mt-2 ${isAr ? "font-arabic" : "font-display"} text-2xl`}
                style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "أن نكون المعيار في الخليج" : "To set the standard in the Gulf"}
            </h2>
            <p className={`mt-5 ${isAr ? "font-arabic" : ""} leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
              {isAr
                ? "أن نكون المرجع القانوني الأول في الكويت والخليج لكل من يبحث عن استشارة عميقة، تقنية حديثة، وتمثيل قانوني يتمتع بسمعة لا تشوبها شائبة."
                : "To be the first legal reference in Kuwait and the Gulf for anyone seeking deep counsel, modern technology, and impeccable representation."}
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow">{isAr ? "قيمنا" : "Values"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "خمس قيم لا نتنازل عنها" : "Five non-negotiable values"}
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              [Lock, isAr ? "السرية" : "Confidentiality", isAr ? "ما يقال داخل المكتب يبقى داخل المكتب." : "What is said inside the firm stays inside the firm."],
              [Award, isAr ? "الدقة" : "Precision", isAr ? "كل موقف يخضع لاختبار النص والاجتهاد." : "Every position stress-tested against statute and case law."],
              [Clock, isAr ? "السرعة" : "Speed", isAr ? "إجراء فوري حين يتطلب الأمر، دون تأخير." : "Immediate action when timing matters."],
              [ShieldCheck, isAr ? "الاحتراف" : "Professionalism", isAr ? "تعامل بنزاهة وأخلاق المهنة في كل ملف." : "Integrity and professional ethics on every file."],
              [Sparkles, isAr ? "الوضوح" : "Clarity", isAr ? "نشرح القانون بلغة بسيطة وقابلة للتنفيذ." : "We explain law in plain, actionable language."],
            ].map(([Icon, title, body]) => (
              <div key={title} className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
                     style={{ background: "var(--bg)", color: "var(--gold-deep)", border: "1px solid var(--gold)" }}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className={`mt-4 ${isAr ? "font-arabic" : "font-display"} text-base`}
                    style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>{title}</h3>
                <p className={`mt-2 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`} style={{ color: "var(--ink-soft)" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">{isAr ? "الفريق" : "The team"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "محامون ومستشارون مرخصون في الكويت" : "Kuwait-licensed lawyers & counsel"}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m) => (
              <div key={m.name_en} className="luxe-card overflow-hidden">
                <div className="aspect-[3/4] overflow-hidden" style={{ background: "var(--beige)" }}>
                  <img src={m.photo} alt={isAr ? m.name_ar : m.name_en}
                       loading="lazy"
                       className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gold-deep)" }}>
                    {isAr ? m.role_ar : m.role_en}
                  </p>
                  <h3 className={`mt-2 ${isAr ? "font-arabic" : "font-display"} text-lg leading-tight`}
                      style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                    {isAr ? m.name_ar : m.name_en}
                  </h3>
                  <p className={`mt-2 ${isAr ? "font-arabic" : ""} text-xs`} style={{ color: "var(--ink-soft)" }}>
                    {isAr ? m.focus_ar : m.focus_en}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y" style={{ background: "var(--navy)", color: "#fff" }}>
        <div className="container-luxe text-center max-w-2xl">
          <h2 className={`${isAr ? "font-arabic" : "font-display"}`}
              style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: isAr ? 800 : 500 }}>
            {isAr ? "هل لديك قضية تستحق المراجعة؟" : "Have a matter worth reviewing?"}
          </h2>
          <p className="mt-5 text-lg" style={{ color: "rgba(245,241,232,0.78)" }}>
            {isAr ? "احجز جلسة سرية لمدة 30 دقيقة، 50 د.ك." : "Book a confidential 30-minute session — KWD 50."}
          </p>
          <a
            href={`https://wa.me/96569656963?text=${encodeURIComponent(isAr ? "مرحباً، أرغب في حجز جلسة قانونية." : "Hello, I'd like to book a legal session.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-8"
          >
            <Button className="btn-gold rounded-sm px-8 h-12">
              {isAr ? "احجز الآن عبر واتساب" : "Book via WhatsApp"}
              {isAr ? <ArrowLeft className="w-4 h-4 ms-2" /> : <ArrowRight className="w-4 h-4 ms-2" />}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
